"use client";
/**
 * Daily progress through the trading flow (see lib/flow/steps). Tracks which
 * steps you've completed and where you currently are, keyed to the ET trading
 * date so it resets each morning on its own. Persisted via safeLocalStorageSet
 * (syncs with the user's other prefs) and broadcast on a custom event so the
 * /flow page and the top-bar rail stay in lockstep.
 */
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import { FLOW_STEPS } from "@/lib/flow/steps";

const KEY = "mpp:flow:progress:v1";
export const FLOW_PROGRESS_EVENT = "mpp:flow:progress";

export type FlowProgress = {
  date: string; // ET calendar date "YYYY-MM-DD"
  completed: string[]; // step ids done today
  current: string | null; // step id you're on
};

/** Today's ET date, so the flow resets at the New York day boundary. */
export function etToday(): string {
  try {
    return new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function loadProgress(): FlowProgress {
  const today = etToday();
  try {
    const raw = safeLocalStorageGet(KEY);
    if (raw) {
      const p = JSON.parse(raw) as FlowProgress;
      if (p && p.date === today && Array.isArray(p.completed)) {
        return { date: today, completed: p.completed.filter((id) => FLOW_STEPS.some((s) => s.id === id)), current: p.current ?? null };
      }
    }
  } catch {
    /* fall through to a fresh day */
  }
  return { date: today, completed: [], current: null };
}

function save(p: FlowProgress): void {
  safeLocalStorageSet(KEY, JSON.stringify(p));
  try {
    window.dispatchEvent(new CustomEvent(FLOW_PROGRESS_EVENT));
  } catch {
    /* SSR */
  }
}

export function subscribeProgress(fn: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(FLOW_PROGRESS_EVENT, fn);
  window.addEventListener("storage", fn); // cross-tab
  return () => {
    window.removeEventListener(FLOW_PROGRESS_EVENT, fn);
    window.removeEventListener("storage", fn);
  };
}

export function isDone(id: string): boolean {
  return loadProgress().completed.includes(id);
}

export function toggleDone(id: string): FlowProgress {
  const p = loadProgress();
  const completed = p.completed.includes(id) ? p.completed.filter((x) => x !== id) : [...p.completed, id];
  const next = { ...p, completed };
  save(next);
  return next;
}

export function markDone(id: string): FlowProgress {
  const p = loadProgress();
  if (p.completed.includes(id)) return p;
  const next = { ...p, completed: [...p.completed, id] };
  save(next);
  return next;
}

/** Enter a step: remember it as where you are (drives the rail + "continue"). */
export function setCurrent(id: string | null): FlowProgress {
  const next = { ...loadProgress(), current: id };
  save(next);
  return next;
}

export function resetToday(): FlowProgress {
  const next: FlowProgress = { date: etToday(), completed: [], current: null };
  save(next);
  return next;
}

/** The next step to act on: the first, in flow order, not yet completed. When
 *  `after` is given, only steps ordered strictly after it are considered (used
 *  by the rail to point forward from where you are). */
export function nextStep(p: FlowProgress, after?: string | null): string | null {
  const afterN = after ? FLOW_STEPS.find((s) => s.id === after)?.n ?? 0 : 0;
  const candidate = FLOW_STEPS.find((s) => !p.completed.includes(s.id) && s.n > afterN);
  return candidate?.id ?? FLOW_STEPS.find((s) => !p.completed.includes(s.id))?.id ?? null;
}
